import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import OCRPatente from '../components/OCRPatente';
import { api } from '../api/client';

type Consulta = {
  patente: string;
  esta_pagando: boolean;
  sesion_activa: {
    id: number;
    zona_nombre: string;
    zona_codigo: string;
    iniciada_en: string;
    tarifa_hora_centavos: number;
  } | null;
  vehiculo_conocido: {
    tipo: string;
    alias: string | null;
    ciudadano_id: number;
    titular: string;
  } | null;
};

export default function ConsultarPatente() {
  const nav = useNavigate();
  const [patente, setPatente] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<Consulta | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function consultar() {
    if (patente.length < 6) {
      setErr('Patente muy corta');
      return;
    }
    setLoading(true);
    setErr(null);
    setRes(null);
    try {
      const data = await api<Consulta>(`/inspector/consultar-patente?patente=${encodeURIComponent(patente)}`);
      setRes(data);
    } catch (e: any) {
      setErr(e.message || 'Error consultando');
    } finally {
      setLoading(false);
    }
  }

  const minutosTranscurridos = res?.sesion_activa
    ? Math.floor((Date.now() - new Date(res.sesion_activa.iniciada_en.replace(' ', 'T')).getTime()) / 60000)
    : 0;

  return (
    <>
      <TopBar title="Consultar patente" back />
      <main className="px-4 py-5 space-y-4 max-w-md mx-auto pb-24">
        <OCRPatente value={patente} onChange={setPatente} />

        <button
          onClick={consultar}
          disabled={loading || patente.length < 6}
          className="w-full bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white rounded-xl py-3 font-bold"
        >
          {loading ? 'Consultando…' : 'Consultar'}
        </button>

        {err && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-xl p-3">
            {err}
          </div>
        )}

        {res && (
          <div className="space-y-3 pt-2">
            <div
              className={`rounded-2xl p-5 text-center border-2 ${
                res.esta_pagando
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                {res.patente}
              </p>
              <p className={`text-2xl font-extrabold mt-2 ${
                res.esta_pagando ? 'text-green-700' : 'text-red-700'
              }`}>
                {res.esta_pagando ? '✓ Está pagando' : '✗ NO está pagando'}
              </p>

              {res.sesion_activa && (
                <div className="mt-3 text-sm text-gray-700 space-y-1">
                  <p>Zona <strong>{res.sesion_activa.zona_codigo}</strong> — {res.sesion_activa.zona_nombre}</p>
                  <p>{minutosTranscurridos} min transcurridos · ${(res.sesion_activa.tarifa_hora_centavos / 100).toFixed(2)}/h</p>
                </div>
              )}
            </div>

            {res.vehiculo_conocido && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Titular</p>
                <p className="font-semibold text-gray-900">{res.vehiculo_conocido.titular}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {res.vehiculo_conocido.tipo}{res.vehiculo_conocido.alias ? ` · "${res.vehiculo_conocido.alias}"` : ''}
                </p>
              </div>
            )}

            {!res.esta_pagando && (
              <button
                onClick={() => nav('/labrar', { state: { patente: res.patente, sesion_id: null } })}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-3 font-bold"
              >
                📝 Labrar acta sobre esta patente
              </button>
            )}
          </div>
        )}
      </main>
    </>
  );
}
